"use client"

import { useEffect, useState, useTransition } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { X, ChevronDown } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  POSITION_TYPE_OPTIONS,
  COMPENSATION_OPTIONS,
  EXPERIENCE_OPTIONS,
} from "@/components/hiring/hiring-form"

// Hourly and salary are still valid on a posting (the form and the labels
// keep them for existing rows) but aren't how barbers look for a chair, so
// the filter only offers the three that are.
const PAY_FILTER_OPTIONS = COMPENSATION_OPTIONS.filter(o => o.value !== "hourly" && o.value !== "salary")

// City and Filters are the standard filter-bar controls, the same pills
// Browse Shops (/listings) uses: 11px medium text, a chevron, filled
// foreground when something is narrowing the board.
const CONTROL = "inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors"
const CONTROL_ON = "bg-foreground text-background"

// Chips inside the panel carry one colour per group, so position, pay,
// recency and experience read as four questions at a glance; the group
// labels say the same thing in words for anyone who doesn't see the colour.
// Selected deepens the tint and the edge rather than switching to black,
// so a chosen chip still says which group it belongs to.
type Tone = "amber" | "sky" | "rose" | "zinc"
const TONES: Record<Tone, { off: string; on: string }> = {
  amber: {
    off: "border-amber-100 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20",
    on: "border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-400/60 dark:bg-amber-500/25 dark:text-amber-200",
  },
  sky: {
    off: "border-sky-100 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/20",
    on: "border-sky-400 bg-sky-100 text-sky-900 dark:border-sky-400/60 dark:bg-sky-500/25 dark:text-sky-200",
  },
  rose: {
    off: "border-rose-100 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20",
    on: "border-rose-400 bg-rose-100 text-rose-900 dark:border-rose-400/60 dark:bg-rose-500/25 dark:text-rose-200",
  },
  zinc: {
    off: "border-zinc-200 bg-zinc-50 text-zinc-700 hover:bg-zinc-100 dark:border-zinc-500/25 dark:bg-zinc-500/10 dark:text-zinc-300 dark:hover:bg-zinc-500/20",
    on: "border-zinc-500 bg-zinc-200 text-zinc-900 dark:border-zinc-400/60 dark:bg-zinc-500/30 dark:text-zinc-100",
  },
}

// Chip rows scroll sideways on a phone instead of wrapping — a lone
// "Master" on its own line was the giveaway — and wrap from md up.
const CHIP_ROW = "-mx-2.5 flex gap-1 overflow-x-auto px-2.5 scrollbar-hide md:mx-0 md:flex-wrap md:overflow-visible md:px-0"

const POSTED_WITHIN_OPTIONS = [
  { value: "1", label: "Last 24 hours" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
]

// URL-driven filters for /hiring. Each pill toggle pushes the new state
// into the query string; the server-rendered page re-runs its query
// against searchParams. Keeps the URL shareable + SEO-friendly.
//
// `cities` is the list of unique cities that actually have live
// listings — we render those as tappable pills so users get a real
// surface area instead of a free-text guessing game. A datalist on
// the text fallback also offers autocomplete.
//
// Controlled mode (`open` + `onOpenChange`) lets a parent split the
// trigger and chip-panel into separate layout slots. Pass `slot` to
// render only one half: 'trigger' for the Filters button row,
// 'panel' for the chip rows (visible when open). Default renders
// both stacked — preserves the original embedded usage.
export function HiringFilters({
  cities = [],
  open: controlledOpen,
  onOpenChange,
  slot,
}: {
  cities?: string[]
  open?: boolean
  onOpenChange?: (open: boolean) => void
  slot?: "trigger" | "panel" | "city"
}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()
  // 'showFilters' gates the entire pill section. Closed by default —
  // tap the Filters pill to reveal everything.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const showFilters = controlledOpen ?? uncontrolledOpen
  const setShowFilters = (next: boolean | ((v: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(showFilters) : next
    if (onOpenChange) onOpenChange(value)
    else setUncontrolledOpen(value)
  }
  const [city, setCity] = useState(searchParams.get("city") || "")

  // Read current filter state from the URL
  const positionTypes = searchParams.getAll("position")
  const compensation = searchParams.get("compensation") || ""
  const specialties = searchParams.getAll("specialty")
  const experience = searchParams.get("experience") || ""
  const postedWithin = searchParams.get("posted") || ""

  const hasActive =
    !!searchParams.get("city") ||
    positionTypes.length > 0 ||
    !!compensation ||
    specialties.length > 0 ||
    !!experience ||
    !!postedWithin

  const updateUrl = (next: URLSearchParams) => {
    const qs = next.toString()
    startTransition(() => {
      router.push(qs ? `/hiring?${qs}` : "/hiring", { scroll: false })
    })
  }

  const toggleArray = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    const cur = next.getAll(key)
    next.delete(key)
    const set = new Set(cur)
    if (set.has(value)) set.delete(value)
    else set.add(value)
    set.forEach(v => next.append(key, v))
    updateUrl(next)
  }

  const toggleSingle = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (next.get(key) === value) next.delete(key)
    else next.set(key, value)
    updateUrl(next)
  }

  // Debounced city push so each keystroke doesn't trigger a re-fetch
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (city.trim()) next.set("city", city.trim())
      else next.delete("city")
      // Normalize both sides: get() returns null for an absent param, so
      // comparing "" against null fired a spurious router.push on every
      // mount — an invisible self-navigation on /hiring that added a
      // phantom history entry (Back needed two presses).
      const cur = searchParams.get("city") || ""
      if (cur === (next.get("city") || "")) return
      updateUrl(next)
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city])

  const clearAll = () => {
    setCity("")
    updateUrl(new URLSearchParams())
  }

  // Shown on the Filters trigger, the way /listings counts its filters.
  const activeCount =
    (searchParams.get("city") ? 1 : 0) +
    positionTypes.length +
    (compensation ? 1 : 0) +
    specialties.length +
    (experience ? 1 : 0) +
    (postedWithin ? 1 : 0)

  const Pill = ({
    label, selected, onClick, tone,
  }: { label: string; selected: boolean; onClick: () => void; tone: Tone }) => (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`inline-flex h-7 shrink-0 items-center rounded-full border px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors ${
        selected ? TONES[tone].on : TONES[tone].off
      }`}
    >
      {label}
    </button>
  )

  // Slot renderers — let parent split trigger and panel into different
  // layout positions (e.g. /hiring puts the trigger inline with the
  // open-count row, chips below).
  const triggerNode = (
    // No wrap: on /hiring this sits on one line with the heading and the
    // view toggle. On a phone there's no room for Clear on that line, so it
    // lives at the bottom of the open panel there; "Filtering…" is a
    // screen-reader status rather than another word on the row.
    <div className="flex min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={() => setShowFilters(v => !v)}
        aria-expanded={showFilters}
        className={`${CONTROL} ${showFilters || hasActive ? CONTROL_ON : "bg-muted text-muted-foreground hover:text-foreground"}`}
      >
        Filters
        {activeCount > 0 && <span className="tabular-nums">{activeCount}</span>}
        <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {hasActive && (
        <button
          type="button"
          onClick={clearAll}
          aria-label="Clear filters"
          className="hidden h-7 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:inline-flex"
        >
          <X className="h-3 w-3" />
          <span className="hidden sm:inline">Clear</span>
        </button>
      )}
      <span role="status" className="sr-only">{isPending ? "Filtering…" : ""}</span>
    </div>
  )

  // City dropdown extracted into its own slot so the listings page can
  // surface it inline under the heading (no need to open the filter
  // panel to scope by city). Falls back to null when there are no
  // cities — keeps the layout clean when there are zero live listings.
  // City dropdown — rendered as inline text with a chevron, not a pill.
  // City and Filters are one pair of controls and now read as one pair:
  // same pill, same height, same type size, sitting side by side.
  //
  // This was a native <select> overlaid invisibly on a text label. That
  // made the one control on the row that opens the OS picker — grey
  // system chrome on iOS, a different list on every platform, and nothing
  // the theme touches — while the Filters button beside it was ours. The
  // project's Select renders our own listbox, follows dark mode, and is
  // keyboard navigable without the invisible-overlay trick.
  //
  // Styled as the standard filter-bar control (see CONTROL): "City" with a
  // chevron when unset, the city name filled foreground when set, exactly
  // like the City trigger on /listings.
  const cityActive = !!city
  const cityNode = cities.length > 0 ? (
    // value "" (no city) shows the "City" placeholder, matching the
    // /listings trigger; picking "All cities" clears back to it.
    <Select value={city} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
      <SelectTrigger
        aria-label="Filter by city"
        className={`${CONTROL} w-auto max-w-[5.5rem] sm:max-w-[8.5rem] border-0 [&>span]:truncate py-0 shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-1 focus-visible:ring-ring data-[size=default]:h-7 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-100 ${
          cityActive
            ? `${CONTROL_ON} dark:bg-foreground dark:hover:bg-foreground`
            : "bg-accent text-foreground hover:bg-accent/70 dark:bg-accent dark:hover:bg-accent/70"
        }`}
      >
        <SelectValue placeholder="City" />
      </SelectTrigger>
      {/* Same rounded panel + pill rows as the other menus (search sort,
          account) rather than the default square listbox. */}
      <SelectContent className="max-h-72 rounded-[22px] border-0 p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.18)] ring-[0.5px] ring-foreground/10">
        <SelectItem value="all" className="rounded-full py-1.5 pl-3 text-xs">All cities</SelectItem>
        {cities.map(c => (
          <SelectItem key={`city-${c}`} value={c} className="rounded-full py-1.5 pl-3 text-xs">{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : null

  if (slot === "trigger") return triggerNode
  if (slot === "city") return cityNode
  if (slot === "panel") {
    if (!showFilters) return null
    return (
      <div className="space-y-2.5 pt-1">
        {/* Each group is labelled. Colour alone was carrying the meaning —
            amber for position, sky for pay, rose for recency, violet for
            specialty, zinc for experience — which only works if you already
            know the code. Twenty-five undifferentiated chips also read as one
            wall rather than five questions. */}
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Position</p>
          <div className={CHIP_ROW}>
            {POSITION_TYPE_OPTIONS.map(o => (
              <Pill key={`pos-${o.value}`} label={o.label} selected={positionTypes.includes(o.value)} onClick={() => toggleArray("position", o.value)} tone="amber" />
            ))}
          </div>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Pay</p>
          <div className={CHIP_ROW}>
            {PAY_FILTER_OPTIONS.map(o => (
              <Pill key={`comp-${o.value}`} label={o.label} selected={compensation === o.value} onClick={() => toggleSingle("compensation", o.value)} tone="sky" />
            ))}
          </div>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Posted</p>
          <div className={CHIP_ROW}>
            {POSTED_WITHIN_OPTIONS.map(o => (
              <Pill key={`pw-${o.value}`} label={o.label} selected={postedWithin === o.value} onClick={() => toggleSingle("posted", o.value)} tone="rose" />
            ))}
          </div>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Experience</p>
          <div className={CHIP_ROW}>
            {EXPERIENCE_OPTIONS.map(o => (
              <Pill key={`exp-${o.value}`} label={o.label} selected={experience === o.value} onClick={() => toggleSingle("experience", o.value)} tone="zinc" />
            ))}
          </div>
        </div>
        {hasActive && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:hidden"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>
    )
  }

  // Default: full embedded mode (legacy callers). Trigger row + chip
  // panel stacked inside a sticky shell.
  return (
    <div
      className="space-y-3 sticky top-0 z-30 sm:relative sm:top-auto sm:z-auto -mx-4 sm:mx-0 px-4 sm:px-0 py-3 sm:py-0 bg-gray-100/95 sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none"
    >
      {/* Top row — Filters pill (always visible) + Clear when active */}
      {triggerNode}

      {/* All filter rows hidden until the Filters pill is toggled on */}
      {showFilters && (
        <div className="space-y-3 pt-1">
          {/* City. The second copy of this control — the row above has one
              too — so it is the same Select, not a second native picker
              dressed to look similar. */}
          {cities.length > 0 && cityNode}
          <div className={CHIP_ROW}>
            {POSITION_TYPE_OPTIONS.map(o => (
              <Pill key={`pos-${o.value}`} label={o.label} selected={positionTypes.includes(o.value)} onClick={() => toggleArray("position", o.value)} tone="amber" />
            ))}
          </div>
          <div className={CHIP_ROW}>
            {PAY_FILTER_OPTIONS.map(o => (
              <Pill key={`comp-${o.value}`} label={o.label} selected={compensation === o.value} onClick={() => toggleSingle("compensation", o.value)} tone="sky" />
            ))}
          </div>
          <div className={CHIP_ROW}>
            {POSTED_WITHIN_OPTIONS.map(o => (
              <Pill key={`pw-${o.value}`} label={o.label} selected={postedWithin === o.value} onClick={() => toggleSingle("posted", o.value)} tone="rose" />
            ))}
          </div>
          <div className={CHIP_ROW}>
            {EXPERIENCE_OPTIONS.map(o => (
              <Pill key={`exp-${o.value}`} label={o.label} selected={experience === o.value} onClick={() => toggleSingle("experience", o.value)} tone="zinc" />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
